import { TextField, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { ModalContainer } from 'src/components/ModalContainer';
import { useCgCustomDomains } from 'src/graphql/entities/cg-custom-domain/use-cg-custom-domain';
import { useManageCgCustomDomain } from 'src/graphql/entities/cg-custom-domain/use-manage-cg-custom-domain';
import { usePartnerDomains } from 'src/graphql/entities/partner-domain';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { FALLBACK_EXAMPLE_APEX } from './cg-custom-domain-display';
import { getGraphQLErrorMessage } from './get-graphql-error-message';
import {
  getEmailDomainWarning,
  parseCustomDomainHostname,
  suggestHostname,
} from './parse-custom-domain-hostname';
import { mutedSx } from './styles';

const DEFAULT_HELP =
  'The address clients will see. Use a subdomain, not the root of your domain.';

export const AddCgCustomDomainModal = () => {
  const { closeModal } = useModalMethods(Modals.AddCgCustomDomain);
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { addDomain, isAdding } = useManageCgCustomDomain(partnerId);
  const { partnerDomains } = usePartnerDomains(partnerId);
  const { domains } = useCgCustomDomains(partnerId);
  const { showSuccessAlert } = useNotifications();

  const domainId = useId();
  // Null until the user types, so the suggestion fills in once the partner's domains load.
  const [typedValue, setTypedValue] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const emailDomains = partnerDomains.map(({ domain }) => domain);
  const exampleApex = emailDomains[0] ?? FALLBACK_EXAMPLE_APEX;
  // The first partner domain whose address isn't registered yet; empty once every one is.
  const registered = new Set(domains.map(({ hostname }) => hostname));
  const suggestion =
    emailDomains.map(suggestHostname).find((hostname) => !registered.has(hostname)) ?? '';
  const value = typedValue ?? suggestion;
  const parsed = parseCustomDomainHostname(value, exampleApex);
  const error = serverError ?? parsed.error;
  const canAdd = Boolean(parsed.apex) && !error;

  const handleAdd = async () => {
    try {
      await addDomain(parsed.hostname);
      showSuccessAlert(
        `${parsed.hostname} added. Next, add its DNS record and choose the client segments it serves.`,
      );
      closeModal();
    } catch (addError) {
      setServerError(getGraphQLErrorMessage(addError, 'Couldn’t add the domain. Try again.'));
    }
  };

  return (
    <ModalContainer
      open
      size="small"
      title="Add custom domain"
      actions={[
        { label: 'Cancel', kind: 'secondary' },
        {
          label: 'Add domain',
          kind: 'primary',
          onClick: handleAdd,
          disabled: !canAdd,
          loading: isAdding,
          loadingLabel: 'Adding',
        },
      ]}
      onClose={closeModal}
    >
      <Typography variant="body2" sx={{ ...mutedSx, mb: 2.5 }}>
        Register the address, then choose which client segments use it. Their membership pages,
        client sign-in, and the links we send their clients move to it.
      </Typography>

      <FormFieldLabel htmlFor={domainId}>Domain</FormFieldLabel>
      <TextField
        id={domainId}
        autoFocus
        fullWidth
        placeholder={suggestHostname(exampleApex)}
        value={value}
        onChange={(event) => {
          setTypedValue(event.target.value);
          setServerError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && canAdd && !isAdding) void handleAdd();
        }}
        error={Boolean(error)}
        helperText={error ?? getEmailDomainWarning(parsed.apex, emailDomains) ?? DEFAULT_HELP}
      />
    </ModalContainer>
  );
};
